import React, { useEffect } from "react";
import { SITE_URL } from "../data/business";

/**
 * Seo — updates <head> tags on route change.
 *
 * Sets:
 *  - document.title
 *  - meta name=description
 *  - link rel=canonical
 *  - meta property=og:title / og:description / og:url / og:image / og:type
 *  - meta name=twitter:title / twitter:description / twitter:image / twitter:card
 *  - meta name=robots (per page, optional)
 *  - <script type="application/ld+json" data-lc-jsonld="1"> (replaceable)
 *
 * Inputs:
 *  - title       string (required)
 *  - description string
 *  - canonical   string (full URL — pass an empty string to suppress)
 *  - jsonLd      object | object[] (each must be a non-empty {@type ...} object)
 *  - ogImage     string (defaults to homepage hero)
 *  - robots      string (e.g. "noindex, follow")
 */
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_premium-landscaping-1/artifacts/7ykqp7kz_Screenshot_20260511_183605_Gallery.jpg";

export default function Seo({ title, description, canonical, jsonLd, ogImage, robots }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (key, value, attr = "name") => {
      if (value === undefined || value === null) return;
      let m = document.querySelector(`meta[${attr}="${key}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attr, key);
        document.head.appendChild(m);
      }
      m.setAttribute("content", String(value));
    };

    if (description) setMeta("description", description);
    if (robots) setMeta("robots", robots);

    const ogUrl = canonical || SITE_URL + window.location.pathname;
    const img = ogImage || DEFAULT_OG_IMAGE;

    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", ogUrl, "property");
    setMeta("og:image", img, "property");
    setMeta("og:type", "website", "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", img);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // Strip previous SEO JSON-LD blocks; only inject non-empty objects.
    document.querySelectorAll('script[data-lc-jsonld]').forEach((n) => n.remove());
    const arr = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    arr.filter((o) => o && typeof o === "object" && Object.keys(o).length > 0).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-lc-jsonld", "1");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, canonical, jsonLd, ogImage, robots]);
  return null;
}
